/**
 * device/apps/jukebox.ts -- Jukebox Audio App
 *
 * An ambient audio controller for the in-game device. Displays a
 * now-playing section with procedural album art, toggleable ambient
 * sound layers with volume sliders, mood presets, and a cosmetic
 * frequency visualizer.
 *
 * All state persists in appStates["jukebox"]. Everything is drawn
 * procedurally via Canvas 2D -- no DOM, no audio API. This is a
 * pure visual fiction: the UI of a music player that exists inside
 * the game world.
 */

import type { GameState } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";

// ---- Constants ---------------------------------------------------------------

const APP_ID = "jukebox";
const APP_NAME = "Jukebox";

// Colors -- dark moody audio player aesthetic
const C_BG           = "#0d0b14";
const C_SURFACE      = "#1a1625";
const C_SURFACE_LIT  = "#241f33";
const C_ACCENT       = "#a855f7";
const C_ACCENT_DIM   = "rgba(168,85,247,0.4)";
const C_GREEN        = "#22c55e";
const C_GREEN_DIM    = "rgba(34,197,94,0.25)";
const C_GRAY_TOGGLE  = "#3a3545";
const C_SLIDER_TRACK = "#2a2535";
const C_SLIDER_FILL  = "#a855f7";
const C_TEXT_PRIMARY  = "#f0eaf8";
const C_TEXT_DIM      = "rgba(240,234,248,0.5)";
const C_TEXT_MUTED    = "rgba(240,234,248,0.3)";
const C_PRESET_BG    = "#252030";
const C_PRESET_ACTIVE = "#7c3aed";
const C_VIS_BAR      = "#a855f7";

// Layout
const FONT_SM = "8px sans-serif";
const FONT_MD = "9px sans-serif";
const FONT_LG = "bold 10px sans-serif";
const FONT_XL = "bold 12px sans-serif";

// ---- Audio Layer Definitions -------------------------------------------------

interface LayerDef {
  id: string;
  name: string;
  icon: string;
}

const LAYERS: LayerDef[] = [
  { id: "fire",     name: "Fire Crackle", icon: "\uD83D\uDD25" },
  { id: "rain",     name: "Rain",         icon: "\uD83C\uDF27" },
  { id: "birds",    name: "Birdsong",     icon: "\uD83D\uDC26" },
  { id: "wind",     name: "Wind",         icon: "\uD83C\uDF43" },
  { id: "musicbox", name: "Music Box",    icon: "\uD83C\uDFB5" },
  { id: "ocean",    name: "Ocean Waves",  icon: "\uD83C\uDF0A" },
  { id: "cricket",  name: "Cricket Song", icon: "\uD83E\uDD97" },
  { id: "cafe",     name: "Caf\u00e9 Murmur", icon: "\u2615" },
];

// ---- Mood Presets ------------------------------------------------------------

interface PresetDef {
  id: string;
  label: string;
  /** Which layer IDs are active in this preset */
  layers: string[];
  /** Default volume for each active layer */
  volumes: Record<string, number>;
}

const PRESETS: PresetDef[] = [
  {
    id: "cozy", label: "Cozy",
    layers: ["fire", "rain", "musicbox"],
    volumes: { fire: 0.8, rain: 0.4, musicbox: 0.5 },
  },
  {
    id: "focus", label: "Focus",
    layers: ["rain", "cafe"],
    volumes: { rain: 0.6, cafe: 0.35 },
  },
  {
    id: "chill", label: "Chill",
    layers: ["ocean", "wind", "musicbox"],
    volumes: { ocean: 0.7, wind: 0.3, musicbox: 0.4 },
  },
  {
    id: "nature", label: "Nature",
    layers: ["birds", "wind", "cricket", "ocean"],
    volumes: { birds: 0.6, wind: 0.5, cricket: 0.4, ocean: 0.5 },
  },
  {
    id: "party", label: "Party",
    layers: ["musicbox", "cafe", "fire"],
    volumes: { musicbox: 0.9, cafe: 0.7, fire: 0.4 },
  },
];

// ---- Per-App State -----------------------------------------------------------

interface JukeboxState {
  /** Which layers are currently toggled on */
  activeLayers: Record<string, boolean>;
  /** Volume per layer, 0-1 */
  volumes: Record<string, number>;
  /** Currently active preset id (null if manual) */
  activePreset: string | null;
  /** Is the main track "playing" */
  playing: boolean;
  /** Scroll offset for the layer list (pixels) */
  scrollY: number;
  /** Momentum velocity (px/s) */
  scrollVelocity: number;
  /** Maximum scroll extent */
  maxScrollY: number;
}

function getState(state: GameState): JukeboxState {
  if (!state.player.device.appStates[APP_ID]) {
    const activeLayers: Record<string, boolean> = {};
    const volumes: Record<string, number> = {};
    for (const layer of LAYERS) {
      activeLayers[layer.id] = layer.id === "fire" || layer.id === "musicbox";
      volumes[layer.id] = layer.id === "fire" ? 0.7 : layer.id === "musicbox" ? 0.5 : 0.6;
    }
    state.player.device.appStates[APP_ID] = {
      activeLayers,
      volumes,
      activePreset: null,
      playing: true,
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
    } satisfies JukeboxState;
  }
  return state.player.device.appStates[APP_ID] as JukeboxState;
}

// ---- Helpers -----------------------------------------------------------------

/** Draw a pill-shaped toggle switch */
function drawToggle(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  on: boolean,
): void {
  const r = h / 2;
  const trackColor = on ? C_GREEN : C_GRAY_TOGGLE;
  drawRoundRect(ctx, x, y, w, h, r, trackColor);

  // Knob
  const knobR = r - 1.5;
  const knobCx = on ? x + w - r : x + r;
  const knobCy = y + r;
  ctx.beginPath();
  ctx.arc(knobCx, knobCy, knobR, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
}

/** Draw a horizontal volume slider */
function drawSlider(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number,
  value: number, active: boolean,
): void {
  const trackH = 3;
  const trackY = y - trackH / 2;

  // Track background
  drawRoundRect(ctx, x, trackY, w, trackH, trackH / 2, C_SLIDER_TRACK);

  // Filled portion
  if (value > 0 && active) {
    const fillW = Math.max(trackH, w * value);
    drawRoundRect(ctx, x, trackY, fillW, trackH, trackH / 2, C_SLIDER_FILL);
  } else if (value > 0) {
    const fillW = Math.max(trackH, w * value);
    drawRoundRect(ctx, x, trackY, fillW, trackH, trackH / 2, C_GRAY_TOGGLE);
  }

  // Handle dot
  const handleX = x + w * value;
  const handleR = active ? 4 : 3;
  ctx.beginPath();
  ctx.arc(handleX, y, handleR, 0, Math.PI * 2);
  ctx.fillStyle = active ? C_ACCENT : "#666";
  ctx.fill();
}

/** Check if a point is inside a rectangle */
function hitTest(px: number, py: number, rx: number, ry: number, rw: number, rh: number): boolean {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

// ---- Visualizer Frequencies --------------------------------------------------

/** Frequencies for the 8-bar visualizer (different sine speeds) */
const VIS_FREQS = [2.3, 3.7, 1.8, 4.2, 2.9, 3.4, 1.5, 4.8];
const VIS_PHASES = [0, 0.8, 1.6, 0.3, 2.1, 1.2, 2.8, 0.5];
const VIS_BARS = 8;

// ---- Layout Geometry (computed per frame from rect) --------------------------

interface Layout {
  // Visualizer
  visY: number;
  visH: number;
  // Now Playing
  npY: number;
  npH: number;
  albumX: number;
  albumY: number;
  albumSize: number;
  // Controls
  ctrlY: number;
  // Layers section
  layersLabelY: number;
  layersY: number;
  layerRowH: number;
  layersVisibleH: number;
  // Presets section
  presetsLabelY: number;
  presetsY: number;
  presetsH: number;
  // Shared
  padX: number;
  innerW: number;
}

function computeLayout(rect: Rect): Layout {
  const padX = 8;
  const innerW = rect.w - padX * 2;

  // Visualizer at top
  const visY = rect.y;
  const visH = 22;

  // Now Playing section
  const npY = visY + visH + 2;
  const albumSize = Math.min(48, innerW * 0.3);
  const npH = albumSize + 8;
  const albumX = rect.x + padX;
  const albumY = npY + 4;

  // Playback controls (below now playing info)
  const ctrlY = npY + npH;

  // Layers section
  const layersLabelY = ctrlY + 20;
  const layersY = layersLabelY + 14;
  const layerRowH = 24;

  // Presets section (anchored to bottom)
  const presetsH = 26;
  const presetsLabelY = rect.y + rect.h - presetsH - 14;
  const presetsY = presetsLabelY + 13;

  // Layers visible height = space between layers start and presets label
  const layersVisibleH = presetsLabelY - layersY - 4;

  return {
    visY, visH,
    npY, npH, albumX, albumY, albumSize,
    ctrlY,
    layersLabelY, layersY, layerRowH, layersVisibleH,
    presetsLabelY, presetsY, presetsH,
    padX, innerW,
  };
}

// ---- The App -----------------------------------------------------------------

const jukeboxApp: DeviceApp = {
  id: APP_ID,
  name: APP_NAME,
  iconGradient: ["#a855f7", "#7c3aed"],

  // -- Icon: a music note shape -----------------------------------------------

  drawIcon(ctx, cx, cy, size) {
    const s = size * 0.28;
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    // Note stem
    const stemX = cx + s * 0.3;
    const stemTop = cy - s * 1.0;
    const stemBottom = cy + s * 0.5;
    ctx.beginPath();
    ctx.moveTo(stemX, stemTop);
    ctx.lineTo(stemX, stemBottom);
    ctx.stroke();

    // Note head (filled ellipse)
    ctx.beginPath();
    ctx.ellipse(stemX - s * 0.3, stemBottom, s * 0.4, s * 0.28, -0.4, 0, Math.PI * 2);
    ctx.fill();

    // Flag (curved stroke at top of stem)
    ctx.beginPath();
    ctx.moveTo(stemX, stemTop);
    ctx.bezierCurveTo(
      stemX + s * 0.8, stemTop + s * 0.2,
      stemX + s * 0.6, stemTop + s * 0.8,
      stemX + s * 0.1, stemTop + s * 0.9,
    );
    ctx.lineWidth = 1.5;
    ctx.stroke();
  },

  // -- Content ----------------------------------------------------------------

  drawContent(ctx, rect, state, dt) {
    const s = getState(state);
    const t = state.time.real;
    const L = computeLayout(rect);

    // Background fill
    ctx.fillStyle = C_BG;
    ctx.fillRect(rect.x, rect.y, rect.w, rect.h);

    // ====== Visualizer (top) ======
    drawVisualizer(ctx, rect.x + L.padX, L.visY, L.innerW, L.visH, t, s);

    // ====== Now Playing ======
    drawNowPlaying(ctx, rect, L, t, s);

    // ====== Playback Controls ======
    drawPlaybackControls(ctx, rect, L, s);

    // ====== Section Label: Audio Layers ======
    drawText(ctx, "AUDIO LAYERS", rect.x + L.padX, L.layersLabelY, L.innerW, FONT_SM, C_TEXT_MUTED);

    // ====== Layer List (clipped, scrollable) ======
    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, L.layersY - 1, rect.w, L.layersVisibleH + 2);
    ctx.clip();

    const totalLayersH = LAYERS.length * L.layerRowH;
    const maxScroll = Math.max(0, totalLayersH - L.layersVisibleH);
    s.maxScrollY = maxScroll;

    // ── Momentum / inertia scroll ────────────────────────────────────
    if (Math.abs(s.scrollVelocity) > 0.5) {
      s.scrollY += s.scrollVelocity * dt;
      s.scrollVelocity *= Math.exp(-6 * dt);
    } else {
      s.scrollVelocity = 0;
    }
    s.scrollY = Math.max(0, Math.min(s.scrollY, maxScroll));

    for (let i = 0; i < LAYERS.length; i++) {
      const layer = LAYERS[i];
      const rowY = L.layersY + i * L.layerRowH - s.scrollY;

      // Skip rows outside visible area
      if (rowY + L.layerRowH < L.layersY - 2 || rowY > L.layersY + L.layersVisibleH + 2) continue;

      const isOn = !!s.activeLayers[layer.id];

      // Row background (subtle alternating)
      if (i % 2 === 0) {
        drawRoundRect(ctx, rect.x + L.padX - 2, rowY, L.innerW + 4, L.layerRowH, 4, C_SURFACE);
      }

      // Icon + Name
      const iconX = rect.x + L.padX + 2;
      const nameX = iconX + 14;
      const textCenterY = rowY + L.layerRowH / 2 - 4;

      ctx.font = "10px sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle = isOn ? C_TEXT_PRIMARY : C_TEXT_DIM;
      ctx.fillText(layer.icon, iconX, rowY + L.layerRowH / 2);

      drawText(ctx, layer.name, nameX, textCenterY, 56, FONT_MD, isOn ? C_TEXT_PRIMARY : C_TEXT_DIM);

      // Toggle switch (right side)
      const toggleW = 22;
      const toggleH = 11;
      const toggleX = rect.x + rect.w - L.padX - toggleW;
      const toggleY = rowY + L.layerRowH / 2 - toggleH / 2;
      drawToggle(ctx, toggleX, toggleY, toggleW, toggleH, isOn);

      // Volume slider (between name and toggle)
      const sliderX = nameX + 60;
      const sliderW = toggleX - sliderX - 8;
      const sliderY = rowY + L.layerRowH / 2;
      if (sliderW > 16) {
        drawSlider(ctx, sliderX, sliderY, sliderW, s.volumes[layer.id] ?? 0.5, isOn);
      }
    }

    // Scroll fade at top of layers
    if (s.scrollY > 0) {
      const fadeGrad = ctx.createLinearGradient(0, L.layersY, 0, L.layersY + 10);
      fadeGrad.addColorStop(0, C_BG);
      fadeGrad.addColorStop(1, "rgba(13,11,20,0)");
      ctx.fillStyle = fadeGrad;
      ctx.fillRect(rect.x, L.layersY - 1, rect.w, 12);
    }

    // Scroll fade at bottom of layers
    if (s.scrollY < maxScroll) {
      const fadeGrad2 = ctx.createLinearGradient(0, L.layersY + L.layersVisibleH - 10, 0, L.layersY + L.layersVisibleH);
      fadeGrad2.addColorStop(0, "rgba(13,11,20,0)");
      fadeGrad2.addColorStop(1, C_BG);
      ctx.fillStyle = fadeGrad2;
      ctx.fillRect(rect.x, L.layersY + L.layersVisibleH - 10, rect.w, 12);
    }

    ctx.restore();

    // ====== Section Label: Mood Presets ======
    drawText(ctx, "MOOD PRESETS", rect.x + L.padX, L.presetsLabelY, L.innerW, FONT_SM, C_TEXT_MUTED);

    // ====== Preset Buttons ======
    drawPresets(ctx, rect, L, s);
  },

  // -- Tap handling -----------------------------------------------------------

  handleTap(localX, localY, state) {
    // localX/localY are relative to the content rect origin
    // We need to reconstruct the layout using the same rect the overlay provides.
    // handleTap receives coords relative to content area, so reconstruct a
    // dummy rect starting at 0,0 with the appropriate dimensions.
    // The overlay passes localX relative to screenX and localY relative to
    // contentY + NAV_BAR_H. We use the phone's content width as approximation.
    const contentRect = state.player.device.appContentRect;
    const rect: Rect = contentRect
      ? { x: 0, y: 0, w: contentRect.w, h: contentRect.h }
      : { x: 0, y: 0, w: 172, h: 248 };
    const L = computeLayout(rect);
    const s = getState(state);

    // ---- Playback controls ----
    const ctrlCenterX = rect.w / 2;
    const ctrlBtnY = L.ctrlY;
    const btnSize = 14;

    // Skip back
    if (hitTest(localX, localY, ctrlCenterX - 36 - btnSize / 2, ctrlBtnY, btnSize, btnSize)) {
      // No-op for now (cosmetic skip back)
      return true;
    }
    // Play/Pause
    if (hitTest(localX, localY, ctrlCenterX - 10, ctrlBtnY - 2, 20, 18)) {
      s.playing = !s.playing;
      return true;
    }
    // Skip forward
    if (hitTest(localX, localY, ctrlCenterX + 22, ctrlBtnY, btnSize, btnSize)) {
      // No-op for now (cosmetic skip forward)
      return true;
    }

    // ---- Layer toggles and sliders ----
    for (let i = 0; i < LAYERS.length; i++) {
      const layer = LAYERS[i];
      const rowY = L.layersY + i * L.layerRowH - s.scrollY;

      // Skip rows not in visible area
      if (rowY + L.layerRowH < L.layersY || rowY > L.layersY + L.layersVisibleH) continue;

      if (localY >= rowY && localY < rowY + L.layerRowH) {
        // Toggle switch area (right side)
        const toggleW = 22;
        const toggleX = rect.w - L.padX - toggleW;

        if (localX >= toggleX - 4) {
          s.activeLayers[layer.id] = !s.activeLayers[layer.id];
          s.activePreset = null; // manual change clears preset
          return true;
        }

        // Volume slider area
        const nameX = L.padX + 2 + 14;
        const sliderX = nameX + 60;
        const sliderW = toggleX - sliderX - 8;

        if (localX >= sliderX && localX <= sliderX + sliderW && sliderW > 16) {
          const newVol = Math.max(0, Math.min(1, (localX - sliderX) / sliderW));
          s.volumes[layer.id] = Math.round(newVol * 20) / 20; // quantize to 5% steps
          s.activePreset = null;
          return true;
        }

        // Tapping the row name/icon area also toggles
        if (localX < sliderX) {
          s.activeLayers[layer.id] = !s.activeLayers[layer.id];
          s.activePreset = null;
          return true;
        }
      }
    }

    // ---- Preset buttons ----
    const presetCount = PRESETS.length;
    const presetGap = 4;
    const totalGap = presetGap * (presetCount - 1);
    const presetBtnW = (L.innerW - totalGap) / presetCount;
    const presetBtnH = L.presetsH - 4;

    for (let i = 0; i < presetCount; i++) {
      const preset = PRESETS[i];
      const bx = L.padX + i * (presetBtnW + presetGap);
      const by = L.presetsY;

      if (hitTest(localX, localY, bx, by, presetBtnW, presetBtnH)) {
        applyPreset(s, preset);
        return true;
      }
    }

    return false;
  },

  // -- Drag scroll -------------------------------------------------------------

  handleDrag(dy, state) {
    const s = getState(state);
    s.scrollY = Math.max(0, Math.min(s.scrollY + dy, s.maxScrollY));
    s.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const s = getState(state);
    s.scrollVelocity = velocityY;
  },

  // -- Lifecycle ---------------------------------------------------------------

  onOpen(state) {
    // Ensure state is initialized
    const s = getState(state);
    s.scrollVelocity = 0;
  },

  badge(_state) {
    return 0;
  },
};

// ---- Drawing Sub-Routines ----------------------------------------------------

function drawVisualizer(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  time: number, s: JukeboxState,
): void {
  const barW = Math.floor(w / VIS_BARS) - 2;
  const gap = 2;
  const totalBarW = barW + gap;
  const startX = x + (w - totalBarW * VIS_BARS + gap) / 2;

  // Count active layers for amplitude scaling
  const activeCount = Object.values(s.activeLayers).filter(Boolean).length;
  const ampScale = s.playing ? Math.min(1, 0.3 + activeCount * 0.1) : 0.05;

  for (let i = 0; i < VIS_BARS; i++) {
    const freq = VIS_FREQS[i];
    const phase = VIS_PHASES[i];

    // Combine multiple sine waves for organic motion
    const wave1 = Math.sin(time * freq + phase);
    const wave2 = Math.sin(time * freq * 0.6 + phase * 1.3) * 0.5;
    const wave3 = Math.sin(time * freq * 1.8 + phase * 0.7) * 0.3;
    const combined = (wave1 + wave2 + wave3) / 1.8;

    // Normalize to 0-1 range and apply amplitude
    const normalised = (combined + 1) / 2;
    const barH = Math.max(2, (h - 4) * normalised * ampScale);

    const bx = startX + i * totalBarW;
    const by = y + h - 2 - barH;

    // Gradient per bar
    const barGrad = ctx.createLinearGradient(bx, by, bx, y + h - 2);
    barGrad.addColorStop(0, C_VIS_BAR);
    barGrad.addColorStop(1, "rgba(168,85,247,0.3)");
    ctx.fillStyle = barGrad;

    drawRoundRect(ctx, bx, by, barW, barH, 1.5);
    ctx.fill();
  }
}

function drawNowPlaying(
  ctx: CanvasRenderingContext2D,
  rect: Rect, L: Layout,
  time: number, s: JukeboxState,
): void {
  // Album art area (procedural gradient with musical note)
  const ax = L.albumX;
  const ay = L.albumY;
  const aSize = L.albumSize;

  // Gradient background for album art
  const artGrad = ctx.createLinearGradient(ax, ay, ax + aSize, ay + aSize);
  artGrad.addColorStop(0, "#4c1d95");
  artGrad.addColorStop(0.5, "#7c3aed");
  artGrad.addColorStop(1, "#2e1065");
  drawRoundRect(ctx, ax, ay, aSize, aSize, 6, undefined);
  ctx.fillStyle = artGrad;
  ctx.fill();

  // Animated shimmer across album art
  const shimmerX = ax + ((time * 0.3) % 1.6 - 0.3) * aSize;
  const shimmerGrad = ctx.createLinearGradient(shimmerX, ay, shimmerX + aSize * 0.3, ay + aSize);
  shimmerGrad.addColorStop(0, "rgba(255,255,255,0)");
  shimmerGrad.addColorStop(0.5, "rgba(255,255,255,0.06)");
  shimmerGrad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.save();
  ctx.beginPath();
  drawRoundRect(ctx, ax, ay, aSize, aSize, 6);
  ctx.clip();
  ctx.fillStyle = shimmerGrad;
  ctx.fillRect(ax, ay, aSize, aSize);
  ctx.restore();

  // Musical note icon on album art
  const noteCx = ax + aSize / 2;
  const noteCy = ay + aSize / 2;
  const noteScale = aSize * 0.18;

  ctx.fillStyle = "rgba(255,255,255,0.25)";
  ctx.strokeStyle = "rgba(255,255,255,0.25)";
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";

  // Stem
  ctx.beginPath();
  ctx.moveTo(noteCx + noteScale * 0.3, noteCy - noteScale * 1.0);
  ctx.lineTo(noteCx + noteScale * 0.3, noteCy + noteScale * 0.4);
  ctx.stroke();

  // Note head
  ctx.beginPath();
  ctx.ellipse(noteCx, noteCy + noteScale * 0.4, noteScale * 0.35, noteScale * 0.25, -0.3, 0, Math.PI * 2);
  ctx.fill();

  // Flag
  ctx.beginPath();
  ctx.moveTo(noteCx + noteScale * 0.3, noteCy - noteScale * 1.0);
  ctx.bezierCurveTo(
    noteCx + noteScale * 1.0, noteCy - noteScale * 0.7,
    noteCx + noteScale * 0.8, noteCy - noteScale * 0.1,
    noteCx + noteScale * 0.3, noteCy,
  );
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Track info (right of album art)
  const infoX = ax + aSize + 8;
  const infoW = rect.x + rect.w - L.padX - infoX;

  drawText(ctx, "Now Playing", infoX, ay + 1, infoW, FONT_SM, C_TEXT_MUTED);
  drawText(ctx, "Ambient Hearth", infoX, ay + 13, infoW, FONT_LG, C_TEXT_PRIMARY);
  drawText(ctx, "The Room", infoX, ay + 26, infoW, FONT_MD, C_TEXT_DIM);

  // Active layer count as subtitle
  const activeCount = Object.values(s.activeLayers).filter(Boolean).length;
  const layerLabel = activeCount === 1 ? "1 layer" : `${activeCount} layers`;
  drawText(ctx, layerLabel, infoX, ay + 38, infoW, FONT_SM, C_ACCENT_DIM);
}

function drawPlaybackControls(
  ctx: CanvasRenderingContext2D,
  rect: Rect, L: Layout,
  s: JukeboxState,
): void {
  const cx = rect.x + rect.w / 2;
  const cy = L.ctrlY + 7;

  // Skip back (double triangle left)
  ctx.fillStyle = C_TEXT_DIM;
  drawSkipTriangles(ctx, cx - 30, cy, 5, true);

  // Play/Pause (center, larger)
  if (s.playing) {
    // Pause icon (two bars)
    ctx.fillStyle = C_ACCENT;
    const barW = 3;
    const barH = 11;
    const pauseGap = 4;
    drawRoundRect(ctx, cx - pauseGap / 2 - barW, cy - barH / 2, barW, barH, 1, C_ACCENT);
    drawRoundRect(ctx, cx + pauseGap / 2, cy - barH / 2, barW, barH, 1, C_ACCENT);
  } else {
    // Play icon (triangle right)
    ctx.fillStyle = C_ACCENT;
    ctx.beginPath();
    ctx.moveTo(cx - 4, cy - 6);
    ctx.lineTo(cx + 6, cy);
    ctx.lineTo(cx - 4, cy + 6);
    ctx.closePath();
    ctx.fill();
  }

  // Skip forward (double triangle right)
  ctx.fillStyle = C_TEXT_DIM;
  drawSkipTriangles(ctx, cx + 30, cy, 5, false);
}

function drawSkipTriangles(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
  left: boolean,
): void {
  const dir = left ? -1 : 1;
  for (let t = 0; t < 2; t++) {
    const offset = t * size * 0.7 * dir;
    ctx.beginPath();
    ctx.moveTo(cx + offset + size * dir, cy - size);
    ctx.lineTo(cx + offset - size * dir * 0.2, cy);
    ctx.lineTo(cx + offset + size * dir, cy + size);
    ctx.closePath();
    ctx.fill();
  }
}

function drawPresets(
  ctx: CanvasRenderingContext2D,
  rect: Rect, L: Layout,
  s: JukeboxState,
): void {
  const presetCount = PRESETS.length;
  const presetGap = 4;
  const totalGap = presetGap * (presetCount - 1);
  const btnW = (L.innerW - totalGap) / presetCount;
  const btnH = L.presetsH - 4;

  for (let i = 0; i < presetCount; i++) {
    const preset = PRESETS[i];
    const bx = rect.x + L.padX + i * (btnW + presetGap);
    const by = L.presetsY;
    const isActive = s.activePreset === preset.id;

    const bg = isActive ? C_PRESET_ACTIVE : C_PRESET_BG;
    const textColor = isActive ? "#ffffff" : C_TEXT_DIM;

    drawRoundRect(ctx, bx, by, btnW, btnH, btnH / 2, bg);
    if (isActive) {
      // Subtle glow
      ctx.shadowColor = C_ACCENT;
      ctx.shadowBlur = 4;
      drawRoundRect(ctx, bx, by, btnW, btnH, btnH / 2, bg);
      ctx.shadowBlur = 0;
    }

    drawText(
      ctx, preset.label,
      bx + btnW / 2, by + btnH / 2 - 4,
      btnW - 6, FONT_SM, textColor, "center",
    );
  }
}

// ---- Preset Application ------------------------------------------------------

function applyPreset(s: JukeboxState, preset: PresetDef): void {
  // If already active, deactivate the preset (return to manual)
  if (s.activePreset === preset.id) {
    s.activePreset = null;
    return;
  }

  // Turn off all layers, then enable only preset layers
  for (const layer of LAYERS) {
    s.activeLayers[layer.id] = false;
  }
  for (const layerId of preset.layers) {
    s.activeLayers[layerId] = true;
    if (preset.volumes[layerId] !== undefined) {
      s.volumes[layerId] = preset.volumes[layerId];
    }
  }
  s.activePreset = preset.id;
  s.playing = true;
}

// ---- Self-Register -----------------------------------------------------------

registerApp(jukeboxApp);
